const DEFAULT_TTL_MS = 5 * 60 * 1000;

interface CacheEntry<T> {
  expiresAt: number;
  value: T;
}

interface AccountSettingsCacheUser {
  email?: string | null;
  id?: string | number | null;
  sub?: string | null;
  userId?: string | number | null;
}

interface ReadThroughOptions {
  force?: boolean;
  ttlMs?: number;
}

const cache = new Map<string, CacheEntry<unknown>>();
const pending = new Map<string, Promise<unknown>>();

export function getAccountSettingsUserCacheKey(user?: AccountSettingsCacheUser | null): string | null {
  if (!user) return null;
  const id = user.sub || user.id || user.userId || user.email;
  return id ? String(id) : null;
}

export function makeAccountSettingsCacheKey(...parts: Array<string | number | null | undefined>): string | null {
  if (parts.some((part) => part === null || part === undefined || part === '')) return null;
  return parts.map(String).join(':');
}

export function readAccountSettingsCache<T>(key: string | null): T | null {
  if (!key) return null;
  const entry = cache.get(key) as CacheEntry<T> | undefined;
  if (!entry) return null;
  if (entry.expiresAt <= Date.now()) {
    cache.delete(key);
    return null;
  }
  return entry.value;
}

export function writeAccountSettingsCache<T>(key: string | null, value: T, ttlMs = DEFAULT_TTL_MS): void {
  if (!key) return;
  cache.set(key, {
    expiresAt: Date.now() + ttlMs,
    value,
  });
}

export function clearAccountSettingsCache(key?: string | null): void {
  if (!key) {
    cache.clear();
    pending.clear();
    return;
  }
  cache.delete(key);
  pending.delete(key);
}

export async function readThroughAccountSettingsCache<T>(
  key: string | null,
  fetcher: () => Promise<T>,
  options: ReadThroughOptions = {},
): Promise<T> {
  if (!key) return fetcher();
  if (!options.force) {
    const cached = readAccountSettingsCache<T>(key);
    if (cached !== null) return cached;
    const existing = pending.get(key) as Promise<T> | undefined;
    if (existing) return existing;
  }

  const request = fetcher()
    .then((value) => {
      writeAccountSettingsCache(key, value, options.ttlMs);
      return value;
    })
    .finally(() => {
      pending.delete(key);
    });

  pending.set(key, request);
  return request;
}
